import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList } from 'recharts';
import styles from './GstOverview.module.scss';
import { GstOverviewSummary } from '../../../../store/dashboard/gstOverview/types';

interface AnnualTurnoverRangeChartProps {
    summary: GstOverviewSummary;
}

const BAR_COLOR = '#8340dc';

function formatFyShort(fy: string): string {
    const m = fy?.match(/^(\d{4})-(\d{4})$/);
    if (!m) return fy || '';
    return `FY'${m[1].slice(-2)}-${m[2].slice(-2)}`;
}

function AnnualTurnoverRangeChart({ summary }: Readonly<AnnualTurnoverRangeChartProps>) {
    const { latestFy, latestFyTurnoverBand, yoYTurnover } = summary ?? ({} as GstOverviewSummary);

    if (!yoYTurnover || yoYTurnover.length === 0) {
        return (
            <div className={styles.TurnoverChart}>
                <div className={styles.TurnoverChartTitle}>Annual Turnover Range</div>
                <div className={styles.EmptyState}>No turnover data available</div>
            </div>
        );
    }

    // Display strings (turnoverBand) are canonicalised by polus mergeGstOverview
    // (formatTurnoverSlab) — render verbatim here.
    const chartData = yoYTurnover.map((entry) => ({
        fy: formatFyShort(entry.financialYear),
        rawFy: entry.financialYear,
        slabShort: entry.turnoverBand,
        rawBand: entry.turnoverBand,
        value: Number(entry.slabValue) || 0,
    }));

    const CustomXAxisTick = ({ x, y, payload }: any) => (
        <text x={x} y={y + 14} textAnchor="middle" fill="#615e83" fontFamily="Inter, system-ui, sans-serif" fontSize={9.8} fontWeight={400}>
            {payload.value}
        </text>
    );

    return (
        <div className={styles.TurnoverChart}>
            <div className={styles.TurnoverChartTitleRow}>
                <span className={styles.TurnoverChartTitle}>Annual Turnover Range</span>
            </div>
            <div className={styles.TurnoverHeadline}>
                <span className={styles.TurnoverHeadlineValue}>{latestFyTurnoverBand}</span>
                <span className={styles.TurnoverHeadlineFy}>In {formatFyShort(latestFy)}</span>
            </div>

            {chartData.length > 0 ? (
                <div className={styles.BarChartWrap}>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartData} margin={{ top: 36, right: 24, left: 0, bottom: 8 }}>
                            <CartesianGrid vertical={false} stroke="#e5e5ef" strokeDasharray="4 4" strokeWidth={1.22} />
                            <XAxis dataKey="fy" tick={<CustomXAxisTick />} axisLine={false} tickLine={false} interval={0} />
                            <YAxis hide domain={[0, 10]} />
                            <Bar dataKey="value" fill={BAR_COLOR} radius={[6, 6, 0, 0]} maxBarSize={46} isAnimationActive={false}>
                                <LabelList
                                    dataKey="slabShort"
                                    position="top"
                                    offset={12}
                                    content={(props: any) => {
                                        const { x, y, width, value } = props;
                                        if (!value || typeof x !== 'number' || typeof y !== 'number') return null;
                                        return (
                                            <text
                                                x={x + (typeof width === 'number' ? width / 2 : 0)}
                                                y={y - 10}
                                                textAnchor="middle"
                                                fill="#171717"
                                                fontFamily="Gilroy, system-ui, sans-serif"
                                                fontSize={10}
                                                fontWeight={600}
                                                letterSpacing="-0.02em"
                                            >
                                                {value}
                                            </text>
                                        );
                                    }}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <div className={styles.EmptyState}>No annual turnover data available</div>
            )}
        </div>
    );
}

export default AnnualTurnoverRangeChart;
